import { useEffect, useState, useCallback } from 'react';
import {
  Search, AlertCircle, ChevronLeft, ChevronRight,
  Ban, ShieldCheck, UserPlus, Loader2, X, KeyRound,
} from 'lucide-react';
import {
  listAdminAccounts, createAccount,
  type AdminAccountRow,
  type AccountSort,
} from '@/shared/services/adminService';
import { DataTable, type DataTableColumn } from '@/shared/components/ui/DataTable';
import { useAuth } from '@/shared/contexts/AuthContext';
import { useToast } from '@/shared/contexts/ToastContext';

const PAGE_SIZE = 25;

const ACCOUNT_COLS: DataTableColumn<AdminAccountRow>[] = [
  {
    key: 'userNum', label: 'UserNum', width: '80px', sortKey: 'user_num',
    cell: (a) => <span className="text-xs font-mono text-white/55">{a.userNum}</span>,
  },
  {
    key: 'id', label: 'ID', width: '1.5fr', sortKey: 'id',
    cell: (a) => <span className="text-sm font-semibold text-white truncate">{a.id ?? '—'}</span>,
  },
  {
    key: 'userName', label: 'Nome', width: '1.5fr', sortKey: 'user_name',
    cell: (a) => <span className="text-sm text-white/75 truncate">{a.userName ?? '—'}</span>,
  },
  {
    key: 'email', label: 'Email', width: '2fr', sortKey: 'email',
    cell: (a) => <span className="text-xs text-white/55 truncate">{a.email ?? '—'}</span>,
  },
  {
    key: 'status', label: 'Status', width: '100px', sortKey: 'auth_type',
    cell: (a) => a.authType === 2 ? (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold
                       bg-red-500/15 text-red-400 border border-red-500/25">
        <Ban size={11} /> Banido
      </span>
    ) : (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold
                       bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
        <ShieldCheck size={11} /> Ativo
      </span>
    ),
  },
  {
    key: 'online', label: 'Online', width: '80px', sortKey: 'login',
    cell: (a) => a.login === 1 ? (
      <span className="inline-flex items-center gap-1 text-[10px] font-bold text-[#00e1d7]">
        <span className="w-1.5 h-1.5 rounded-full bg-[#00e1d7] shadow-[0_0_6px_#00e1d7]" />
        online
      </span>
    ) : (
      <span className="text-[10px] text-white/35">offline</span>
    ),
  },
  {
    key: 'createDate', label: 'Criada em', width: '140px', sortKey: 'create_date',
    cell: (a) => (
      <span className="text-[11px] text-white/45 font-mono truncate">
        {a.createDate ? new Date(a.createDate).toLocaleDateString('pt-BR') : '—'}
      </span>
    ),
  },
];

export default function AccountsPage() {
  const { hasFeature } = useAuth();
  const canCreate = hasFeature('create_account');
  const [createOpen, setCreateOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState<AccountSort>('user_num');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [items, setItems] = useState<AdminAccountRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { setPage(0); }, [debouncedSearch, sort, order]);

  const fetchPage = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listAdminAccounts({
        skip: page * PAGE_SIZE,
        limit: PAGE_SIZE,
        search: debouncedSearch || undefined,
        sort,
        order,
      });
      setItems(res.items);
      setTotal(res.total);
    } catch (err: any) {
      setError(err?.message || 'Erro ao carregar contas');
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, sort, order]);

  useEffect(() => { fetchPage(); }, [fetchPage]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const canPrev = page > 0;
  const canNext = page + 1 < totalPages;

  const toggleSort = (key: AccountSort) => {
    if (sort === key) setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    else { setSort(key); setOrder('desc'); }
  };

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white">Contas</h1>
          <p className="text-sm text-white/65 mt-1">
            Gerenciar contas de jogadores · {total.toLocaleString('pt-BR')} no total
          </p>
        </div>
        {canCreate && (
          <button
            onClick={() => setCreateOpen(true)}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold
                       bg-emerald-500/10 border border-emerald-500/30 text-emerald-400
                       hover:bg-emerald-500/20 transition cursor-pointer shrink-0"
          >
            <UserPlus size={14} /> Criar conta
          </button>
        )}
      </div>

      <div className="mb-5 relative">
        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por ID, Nome, Email, UserNum, IP (177.135) ou data (30/06/2026)..."
          className="w-full bg-[#161e26] border border-white/[0.06] rounded-lg pl-11 pr-4 py-2.5
                     text-sm text-white placeholder:text-white/40
                     focus:outline-none focus:border-[#00e1d7]/40 transition"
        />
      </div>

      {error && (
        <div className="surface-card rounded-xl p-4 mb-4 flex items-start gap-3 border-red-500/30">
          <AlertCircle size={18} className="text-red-400 shrink-0 mt-0.5" />
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      <DataTable<AdminAccountRow>
        columns={ACCOUNT_COLS}
        rows={items}
        rowKey={(acc) => acc.userNum}
        rowHref={(acc) => `/admin/account/${acc.userNum}`}
        loading={loading}
        empty="Nenhuma conta encontrada"
        sort={{ key: sort, order }}
        onSort={(key) => toggleSort(key as AccountSort)}
        mobileTitle={(acc) => (
          <span className="flex items-center gap-2">
            {acc.id ?? '—'}
            <span className="text-[10px] text-white/45 font-mono">#{acc.userNum}</span>
          </span>
        )}
        mobileSubtitle={(acc) => acc.userName ?? acc.email ?? ''}
      />

      <div className="flex items-center justify-between mt-4 text-xs text-white/55">
        <span>Página {page + 1} de {totalPages}</span>
        <div className="flex gap-2">
          <button
            onClick={() => canPrev && setPage((p) => p - 1)}
            disabled={!canPrev || loading}
            className="px-3 py-1.5 rounded-lg bg-[#161e26] border border-white/[0.06]
                       hover:border-[#00e1d7]/40 disabled:opacity-40 disabled:cursor-not-allowed
                       transition cursor-pointer flex items-center gap-1"
          >
            <ChevronLeft size={14} /> Anterior
          </button>
          <button
            onClick={() => canNext && setPage((p) => p + 1)}
            disabled={!canNext || loading}
            className="px-3 py-1.5 rounded-lg bg-[#161e26] border border-white/[0.06]
                       hover:border-[#00e1d7]/40 disabled:opacity-40 disabled:cursor-not-allowed
                       transition cursor-pointer flex items-center gap-1"
          >
            Próxima <ChevronRight size={14} />
          </button>
        </div>
      </div>

      {createOpen && (
        <CreateAccountModal
          onClose={() => setCreateOpen(false)}
          onCreated={() => { setCreateOpen(false); fetchPage(); }}
        />
      )}
    </div>
  );
}

/* ─────────────────────────────────── Modal: criar conta ──── */

function CreateAccountModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const { notify } = useToast();
  const [form, setForm] = useState({ login: '', password: '', name: '', phone: '' });
  const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof form) => (v: string) => setForm((f) => ({ ...f, [k]: v }));
  const inCls = 'w-full bg-[#0d141b] border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white';

  const create = async () => {
    if (form.login.trim().length < 3) { notify('error', 'Login muito curto (mín. 3)'); return; }
    if (form.password.length < 4) { notify('error', 'Senha muito curta (mín. 4)'); return; }
    if (form.name.trim().length < 2) { notify('error', 'Nome muito curto (mín. 2)'); return; }
    setBusy(true);
    try {
      const res = await createAccount({
        login: form.login.trim(), password: form.password,
        name: form.name.trim(), phone: form.phone.trim(),
      });
      notify('success', res.message || 'Conta criada');
      onCreated();
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div onClick={(e) => e.stopPropagation()}
           className="surface-card rounded-2xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <UserPlus size={18} className="text-emerald-400" /> Criar conta
          </h3>
          <button onClick={onClose} className="text-white/55 hover:text-white cursor-pointer"><X size={18} /></button>
        </div>

        <p className="text-xs text-white/50 mb-4 flex items-center gap-1.5">
          <KeyRound size={13} /> Usa a procedure oficial do servidor (cria warehouse, cash e 7 dias de premium).
        </p>

        <div className="space-y-3">
          <div>
            <label className="text-[11px] text-white/55 mb-1 block">Login (3-32)</label>
            <input type="text" value={form.login} onChange={(e) => set('login')(e.target.value)}
              placeholder="login" className={inCls} autoComplete="off" />
          </div>
          <div>
            <label className="text-[11px] text-white/55 mb-1 block">Senha (mín. 4)</label>
            <input type="text" value={form.password} onChange={(e) => set('password')(e.target.value)}
              placeholder="senha" className={inCls} autoComplete="off" />
          </div>
          <div>
            <label className="text-[11px] text-white/55 mb-1 block">Nome (2-50)</label>
            <input type="text" value={form.name} onChange={(e) => set('name')(e.target.value)}
              placeholder="nome do titular" className={inCls} />
          </div>
          <div>
            <label className="text-[11px] text-white/55 mb-1 block">Telefone (opcional)</label>
            <input type="text" value={form.phone} onChange={(e) => set('phone')(e.target.value)}
              placeholder="(00) 00000-0000" className={inCls} />
          </div>
          <div className="flex gap-2 pt-1">
            <button onClick={onClose}
              className="flex-1 px-4 py-2.5 rounded-lg bg-white/[0.04] text-white/75 hover:bg-white/[0.08] transition cursor-pointer text-sm">
              Cancelar
            </button>
            <button onClick={create} disabled={busy}
              className="flex-1 px-4 py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-white font-bold text-sm transition cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />} Criar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
